import { ThemeableStack, View, Text, Spinner } from '@kit/ui/src'
import { createFileRoute, Outlet, redirect, useRouter, useLocation } from '@tanstack/react-router'
import { WebLayout } from '@web/app/components'
import { useGetPermissionsMeQuery } from '@web/app/features/access/store/accessApi'
import { accessSelectors, IRouteNode } from '@web/app/features/access'
import { authSelectors } from '@web/app/features/auth/store/authSlice'
import { useEffect } from 'react'
import { useSelector } from 'react-redux'

const EXEMPT_PATHS = ['/cuenta/perfil', '/cuenta/cambiarContrasena', '/cuenta/configuracion']

export const Route = createFileRoute('/_index')({
  beforeLoad: ({ context, location }) => {
    if (!context.isAuthenticated) {
      throw redirect({ to: '/sign-in', search: { redirect: location.href } })
    }

    const isPasswordPending = context.userProfile?.password_created === false
    const isAtPasswordPage = location.pathname === '/cuenta/cambiarContrasena'

    if (isPasswordPending && !isAtPasswordPage) {
      throw redirect({ to: '/cuenta/cambiarContrasena', replace: true })
    }
  },
  component: RouteComponent,
})

function isPathAuthorized(pathname: string, routes: IRouteNode[]): boolean {
  for (const route of routes) {
    if (route.to) {
      if (pathname === route.to || pathname.startsWith(`${route.to}/`)) return true
    }
    if (route.children) {
      for (const child of route.children) {
        if (child.to) {
          if (pathname === child.to || pathname.startsWith(`${child.to}/`)) return true
        }
      }
    }
  }
  return false
}

function RouteComponent() {
  const router = useRouter()
  const location = useLocation()
  const isAuthenticated = useSelector(authSelectors.isAuthenticated)
  const routes = useSelector(accessSelectors.routes)

  const { isLoading, isFetching } = useGetPermissionsMeQuery(undefined, {
    skip: !isAuthenticated,
  })

  const userProfile = useSelector(
    (state: any) => state.appApi.queries['getProfile(undefined)']?.data
  )
  const isPasswordPending = userProfile?.password_created === false
  const isAtPasswordPage = location.pathname === '/cuenta/cambiarContrasena'

  // Redirigir si la contraseña pendiente
  useEffect(() => {
    if (isPasswordPending && !isAtPasswordPage) {
      router.navigate({ to: '/cuenta/cambiarContrasena', replace: true })
    }
  }, [isPasswordPending, isAtPasswordPage, router])

  // Redirigir a login al desloguearse
  useEffect(() => {
    if (!isAuthenticated) {
      router.navigate({ to: '/sign-in', replace: true })
    }
  }, [isAuthenticated, router])

  // Redirigir rutas no autorizadas cuando los permisos ya estén listos
  useEffect(() => {
    if (isLoading || isFetching || routes.length === 0) return
    if (EXEMPT_PATHS.some((p) => location.pathname.startsWith(p))) return

    const authorized = isPathAuthorized(location.pathname, routes)
    if (!authorized) {
      router.navigate({ to: '/dashboard', replace: true })
    }
  }, [routes, isLoading, isFetching, location.pathname, router])

  // --- Bloqueo de render hasta tener permisos verificados ---

  // Mientras los permisos cargan, mostrar spinner sin renderizar el Outlet
  const permissionsReady = !isLoading && !isFetching && routes.length > 0
  if (!permissionsReady) {
    return (
      <WebLayout>
        <ThemeableStack f={1} ai="center" jc="center">
          <Spinner size="large" />
        </ThemeableStack>
      </WebLayout>
    )
  }

  // Rutas exentas siempre pasan
  const isExempt = EXEMPT_PATHS.some((p) => location.pathname.startsWith(p))
  const isAuthorized = isExempt || isPathAuthorized(location.pathname, routes)

  // Si no está autorizado, no renderizar el Outlet (el useEffect redirigirá)
  if (!isAuthorized) {
    return (
      <WebLayout>
        <ThemeableStack f={1} ai="center" jc="center">
          <Spinner size="large" />
        </ThemeableStack>
      </WebLayout>
    )
  }

  if (isPasswordPending && !isAtPasswordPage) {
    return (
      <WebLayout>
        <ThemeableStack f={1} ai="center" jc="center">
          <View animation="lazy" enterStyle={{ opacity: 0 }}>
            <Text color="$color10">Redirigiendo a cambio de contraseña obligatorio...</Text>
          </View>
        </ThemeableStack>
      </WebLayout>
    )
  }

  return (
    <WebLayout>
      <ThemeableStack ai={'center'} w="100%">
        <Outlet />
      </ThemeableStack>
    </WebLayout>
  )
}
